import { type FC, useState } from 'react'
import { CheckCircle2, TestTube2, X, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { usePreviewFeatureFlag } from '@/hooks/useFeatureFlags'
import type {
    DevicePlatform,
    FeatureFlag,
    FeatureFlagPreviewResult,
} from '@/types/feature-flag'

interface FlagPreviewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    flag: FeatureFlag | null
}

/**
 * Answers "who does this actually affect?" — runs the real evaluation path for
 * a chosen user and client, without changing anything.
 */
export const FlagPreviewDialog: FC<FlagPreviewDialogProps> = ({
    open,
    onOpenChange,
    flag,
}) => {
    const [userId, setUserId] = useState('')
    const [platform, setPlatform] = useState<DevicePlatform | 'ANY'>('ANY')
    const [appVersion, setAppVersion] = useState('')
    const [result, setResult] = useState<FeatureFlagPreviewResult | null>(null)
    const [error, setError] = useState<string | null>(null)

    // Remounted by the parent on every open, so no stale result carries over.
    const preview = usePreviewFeatureFlag()

    const handleRun = () => {
        if (!flag) return
        setError(null)

        preview.mutate(
            {
                id: flag.id,
                data: {
                    userId: userId.trim() || undefined,
                    platform: platform === 'ANY' ? undefined : platform,
                    appVersion: appVersion.trim() || undefined,
                },
            },
            {
                onSuccess: response => setResult(response.data),
                onError: (mutationError: unknown) => {
                    const message =
                        (
                            mutationError as {
                                response?: { data?: { message?: string } }
                            }
                        )?.response?.data?.message ?? 'Preview failed'
                    setResult(null)
                    setError(message)
                },
            }
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 bg-transparent p-0 shadow-2xl sm:max-w-lg sm:rounded-2xl"
            >
                <div className="flex flex-col overflow-hidden rounded-2xl bg-white">
                    <div className="flex items-start justify-between gap-3 border-b px-6 py-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E5F5F9]">
                                <TestTube2 className="h-5 w-5 text-[#0F172A]" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-[#0F172A]">
                                    Test this flag
                                </h2>
                                <p className="font-mono text-xs text-muted-foreground">
                                    {flag?.key}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onOpenChange(false)}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="space-y-4 px-6 py-5">
                        <div className="space-y-2">
                            <Label htmlFor="preview-user">User ID</Label>
                            <Input
                                id="preview-user"
                                value={userId}
                                placeholder="Leave empty to test as a logged-out client"
                                onChange={event =>
                                    setUserId(event.target.value)
                                }
                            />
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label>Platform</Label>
                                <Select
                                    value={platform}
                                    onValueChange={value =>
                                        setPlatform(
                                            value as DevicePlatform | 'ANY'
                                        )
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ANY">
                                            Not reported
                                        </SelectItem>
                                        <SelectItem value="ANDROID">
                                            Android
                                        </SelectItem>
                                        <SelectItem value="IOS">iOS</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="preview-version">
                                    App version
                                </Label>
                                <Input
                                    id="preview-version"
                                    value={appVersion}
                                    placeholder="1.0.2"
                                    onChange={event =>
                                        setAppVersion(event.target.value)
                                    }
                                />
                            </div>
                        </div>

                        {result && (
                            <div
                                className={`flex items-center gap-3 rounded-xl border p-4 ${
                                    result.enabled
                                        ? 'border-green-200 bg-green-50'
                                        : 'border-gray-200 bg-gray-50'
                                }`}
                            >
                                {result.enabled ? (
                                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                                ) : (
                                    <XCircle className="h-5 w-5 text-gray-500" />
                                )}
                                <div>
                                    <p className="text-sm font-medium text-[#0F172A]">
                                        {result.enabled
                                            ? 'Enabled for this client'
                                            : 'Not enabled for this client'}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {result.evaluatedFor.userId ??
                                            'Anonymous'}
                                        {result.evaluatedFor.platform
                                            ? ` · ${result.evaluatedFor.platform}`
                                            : ''}
                                        {result.evaluatedFor.appVersion
                                            ? ` · v${result.evaluatedFor.appVersion}`
                                            : ''}
                                    </p>
                                </div>
                            </div>
                        )}

                        {error && (
                            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                                {error}
                            </p>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 border-t px-6 py-4">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Close
                        </Button>
                        <Button
                            className="bg-[#17A2B8] hover:bg-[#148c9e]"
                            disabled={preview.isPending}
                            onClick={handleRun}
                        >
                            {preview.isPending ? 'Evaluating...' : 'Evaluate'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
